import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Check, Loader2, ArrowRight, PackageSearch } from "lucide-react";

/**
 * Shown when a customer searches a medication we don't list yet. Reassures them we
 * can source it and captures the medication + first/last name, email + phone so we
 * can follow up with an estimate. Opened via the global "open-med-not-found" event
 * (detail: { medication }). The medication field is freely editable so the customer
 * can type or correct the full name before sending. Strength / dosage form /
 * quantity / pet are captured here too - without them the team has to phone the
 * customer back before it can price anything (see the Zenrelia lead).
 */
const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", phone: "",
  strength: "", dosage_form: "", quantity: "", pet_name: "", pet_type: "",
};

export default function MedNotFoundPopup() {
  const [open, setOpen] = useState(false);
  const [medication, setMedication] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setMedication((e?.detail?.medication || "").trim());
      setForm(EMPTY_FORM);
      setDone(false);
      setOpen(true);
    };
    window.addEventListener("open-med-not-found", handler);
    return () => window.removeEventListener("open-med-not-found", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const close = () => setOpen(false);
  const update = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!medication.trim()) { toast.error("Please enter the medication name."); return; }
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/medication-leads", { ...form, medication: medication.trim() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm sm:px-4" data-testid="med-not-found-popup">
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl">
        <button onClick={close} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-paw-navy flex items-center justify-center shadow" data-testid="med-not-found-close">
          <X className="w-4 h-4" />
        </button>

        <div className="bg-gradient-to-r from-paw-primary to-[#0a4fae] px-6 pt-6 pb-5">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-white/15 ring-1 ring-white/25 mb-3">
            <PackageSearch className="w-5 h-5 text-white" />
          </span>
          <h2 className="font-outfit font-extrabold text-[21px] leading-tight text-white">
            We can source your medication
          </h2>
          <p className="font-manrope text-[13px] text-white/85 mt-1.5">
            Confirm the medication below and leave your details, and we'll contact you shortly with an estimate.
          </p>
        </div>

        <div className="p-6">
          {done ? (
            <div className="text-center py-4">
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-paw-success/10 mx-auto mb-4"><Check className="w-7 h-7 text-paw-success" /></span>
              <h3 className="font-outfit font-bold text-xl text-paw-navy">Thanks, we've got it</h3>
              <p className="font-manrope text-sm text-paw-text-secondary mt-2">Our team will reach out shortly with an estimate for {medication || "your medication"}.</p>
              <button onClick={close} className="mt-6 inline-flex items-center justify-center rounded-full bg-paw-primary hover:bg-paw-primary-hover text-white px-7 py-3 text-sm font-semibold transition-colors" data-testid="med-not-found-done">Done</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="med-not-found-form">
              <div className="space-y-1.5 min-w-0">
                <label className="font-manrope text-[13px] font-medium text-paw-text">Medication you need *</label>
                <input value={medication} onChange={(e) => setMedication(e.target.value)} required placeholder="Full name + strength, e.g. Acetaminophen 500mg" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="med-lead-medication" />
                <p className="font-manrope text-[11px] text-paw-text-muted pl-1">Type or correct the full medication name and strength so we can price it accurately.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 min-w-0">
                  <label className="font-manrope text-[13px] font-medium text-paw-text">First name *</label>
                  <input value={form.first_name} onChange={update("first_name")} required placeholder="Jane" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="med-lead-first" />
                </div>
                <div className="space-y-1.5 min-w-0">
                  <label className="font-manrope text-[13px] font-medium text-paw-text">Last name</label>
                  <input value={form.last_name} onChange={update("last_name")} placeholder="Smith" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="med-lead-last" />
                </div>
              </div>
              <div className="space-y-1.5 min-w-0">
                <label className="font-manrope text-[13px] font-medium text-paw-text">Email *</label>
                <input type="email" value={form.email} onChange={update("email")} required placeholder="you@email.com" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="med-lead-email" />
              </div>
              <div className="space-y-1.5 min-w-0">
                <label className="font-manrope text-[13px] font-medium text-paw-text">Phone</label>
                <input type="tel" value={form.phone} onChange={update("phone")} placeholder="(555) 555-1234" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="med-lead-phone" />
              </div>

              <div className="pt-1">
                <p className="font-manrope text-[12px] font-semibold text-paw-text uppercase tracking-wider">Prescription details</p>
                <p className="font-manrope text-[11px] text-paw-text-muted mt-0.5">Optional, but filling these in means we can price it right away instead of calling you back.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 min-w-0">
                  <label className="font-manrope text-[13px] font-medium text-paw-text">Strength</label>
                  <input value={form.strength} onChange={update("strength")} placeholder="e.g. 4.8 mg" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="med-lead-strength" />
                </div>
                <div className="space-y-1.5 min-w-0">
                  <label className="font-manrope text-[13px] font-medium text-paw-text">Dosage form</label>
                  <select value={form.dosage_form} onChange={update("dosage_form")} className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary bg-white appearance-none" data-testid="med-lead-dosage-form">
                    <option value="">Select</option>
                    <option value="Tablet">Tablet</option>
                    <option value="Capsule">Capsule</option>
                    <option value="Chewable">Chewable</option>
                    <option value="Liquid / Suspension">Liquid / Suspension</option>
                    <option value="Injection">Injection</option>
                    <option value="Topical / Cream">Topical / Cream</option>
                    <option value="Eye drops">Eye drops</option>
                    <option value="Ear drops">Ear drops</option>
                    <option value="Inhaler">Inhaler</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 min-w-0">
                  <label className="font-manrope text-[13px] font-medium text-paw-text">Quantity needed</label>
                  <input value={form.quantity} onChange={update("quantity")} placeholder="e.g. 30 tablets" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="med-lead-quantity" />
                </div>
                <div className="space-y-1.5 min-w-0">
                  <label className="font-manrope text-[13px] font-medium text-paw-text">Pet type</label>
                  <select value={form.pet_type} onChange={update("pet_type")} className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary bg-white appearance-none" data-testid="med-lead-pet-type">
                    <option value="">Select</option>
                    <option value="Dog">Dog</option>
                    <option value="Cat">Cat</option>
                    <option value="Horse">Horse</option>
                    <option value="Bird">Bird</option>
                    <option value="Rabbit">Rabbit</option>
                    <option value="Reptile">Reptile</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5 min-w-0">
                <label className="font-manrope text-[13px] font-medium text-paw-text">Pet name</label>
                <input value={form.pet_name} onChange={update("pet_name")} placeholder="e.g. Eevee" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid="med-lead-pet-name" />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-50 text-white rounded-xl h-12 font-manrope font-semibold text-[15px] transition-colors flex items-center justify-center gap-2 shadow-lg shadow-paw-accent/20" data-testid="med-lead-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Contact me with an estimate <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="font-manrope text-[11px] text-paw-text-muted text-center pt-0.5">No payment, no commitment. We'll just send you a price.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
